import React from 'react';
import { createPortal } from 'react-dom';
import { Lock, ShieldCheck, Video, Camera, Monitor, X } from 'lucide-react';

export const PRIVACY_NOTICE_MESSAGE =
  'Privacy mode is enabled. Recording, screenshots, and screen capture are disabled to protect this meeting.';

/**
 * Shared privacy notice — replaces the "Enable AI Notetaker?" prompt at meeting
 * start and gates every Record / AI Notes / AI Notetaker entry point. The
 * design reads as a hardened security console: animated padlock, scanline,
 * gradient ring, and a status grid that names exactly what is being blocked.
 *
 * Props (unchanged):
 *   open: boolean
 *   onClose: () => void
 *   onDisablePrivacy: () => void
 *   disablePrivacyDisabled?: boolean
 */
export default function PrivacyNoticeModal({
  open,
  onClose,
  onDisablePrivacy,
  disablePrivacyDisabled = false,
}) {
  if (!open) return null;

  const STATUS_ITEMS = [
    { Icon: Video,   label: 'Recording',      tag: 'BLOCKED' },
    { Icon: Camera,  label: 'Screenshots',    tag: 'BLOCKED' },
    { Icon: Monitor, label: 'Screen Capture', tag: 'BLOCKED' },
  ];

  // Portal to body so the modal centers in the viewport, not inside a parent
  // with backdrop-filter / transform that creates a containing block (e.g. the
  // meeting TopBar where one of the trigger buttons lives).
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const modal = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-notice-title"
      data-testid="privacy-notice-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#001233]/85 backdrop-blur-md" onClick={onClose} />

      {/* Modal frame: animated conic-gradient border */}
      <div className="relative w-full max-w-md">
        <div className="rlink-priv-ring rounded-2xl p-[1.5px]">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#04122e] via-[#001233] to-[#000814]">
            {/* Hex / grid pattern overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.18]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(0,200,83,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,83,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage: 'radial-gradient(circle at 50% 30%, black 0%, transparent 75%)',
                WebkitMaskImage: 'radial-gradient(circle at 50% 30%, black 0%, transparent 75%)',
              }}
            />
            {/* Animated scanline */}
            <div className="rlink-priv-scan pointer-events-none absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#00c853] to-transparent opacity-60" />
            {/* Top-right green corner glow */}
            <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#00c853]/25 blur-3xl" />
            {/* Bottom-left purple corner glow */}
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-[#6a1fbf]/30 blur-3xl" />

            {/* Close button (X in corner) */}
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header band */}
            <div className="relative flex items-center justify-between border-b border-white/5 px-5 py-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00c853] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00c853]" />
                </span>
                <span className="font-mono text-[10px] font-semibold tracking-[0.25em] text-[#00c853]">
                  SECURE SESSION
                </span>
              </div>
              <span className="font-mono text-[10px] tracking-widest text-gray-500">AES-256 · TLS 1.3</span>
            </div>

            <div className="relative px-6 pb-6 pt-7">
              {/* Padlock hero */}
              <div className="relative mx-auto mb-5 flex h-32 w-32 items-center justify-center">
                {/* Concentric pulse rings */}
                <span className="rlink-priv-pulse absolute inset-0 rounded-full border border-[#00c853]/40" />
                <span
                  className="rlink-priv-pulse absolute inset-0 rounded-full border border-[#00c853]/30"
                  style={{ animationDelay: '0.6s' }}
                />
                <span
                  className="rlink-priv-pulse absolute inset-0 rounded-full border border-[#00c853]/20"
                  style={{ animationDelay: '1.2s' }}
                />
                {/* Inner orb */}
                <span className="absolute inset-3 rounded-full bg-gradient-to-br from-[#00c853]/30 via-[#00c853]/10 to-transparent shadow-[0_0_60px_-10px_rgba(0,200,83,0.65)]" />
                <span className="absolute inset-5 rounded-full bg-gradient-to-br from-[#04210f] to-[#001b08] ring-1 ring-inset ring-[#00c853]/40" />
                {/* Padlock icon */}
                <Lock
                  className="relative h-12 w-12 text-[#00c853]"
                  strokeWidth={2.25}
                  style={{ filter: 'drop-shadow(0 0 14px rgba(0,200,83,0.85))' }}
                />
                {/* ShieldCheck overlay badge */}
                <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-[#001233] ring-2 ring-[#00c853]/70">
                  <ShieldCheck className="h-4 w-4 text-[#00c853]" />
                </span>
              </div>

              {/* Title */}
              <h2
                id="privacy-notice-title"
                className="text-center text-xl font-bold tracking-tight text-white"
              >
                Privacy Mode <span className="text-[#00c853]">Engaged</span>
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-center text-sm leading-relaxed text-gray-300">
                {PRIVACY_NOTICE_MESSAGE}
              </p>

              {/* Status grid — what's actively blocked */}
              <div className="mt-5 grid grid-cols-3 gap-2">
                {STATUS_ITEMS.map(({ Icon, label, tag }) => (
                  <div
                    key={label}
                    className="group relative overflow-hidden rounded-lg border border-[#00c853]/25 bg-[#001a0b]/40 px-2 py-3 text-center backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#00c853]" />
                    </div>
                    <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-gray-300">
                      {label}
                    </div>
                    <div className="mt-1 flex items-center justify-center gap-1">
                      <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-[#00c853]" />
                      <span className="font-mono text-[9px] font-bold tracking-widest text-[#00c853]">
                        {tag}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action row */}
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-gray-200 transition hover:border-white/20 hover:bg-white/10"
                  data-testid="privacy-notice-close"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={onDisablePrivacy}
                  disabled={disablePrivacyDisabled}
                  className="group relative overflow-hidden rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm font-semibold text-red-300 transition hover:border-red-400/60 hover:bg-red-500/20 hover:text-red-200 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/5 disabled:text-gray-500 disabled:hover:bg-white/5"
                  data-testid="privacy-notice-disable"
                >
                  <span className="relative z-10 flex items-center justify-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" />
                    Disable Privacy
                  </span>
                </button>
              </div>

              {/* Footer micro-status ticker */}
              <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-3 font-mono text-[9px] tracking-widest text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-1 w-1 rounded-full bg-[#00c853]" />
                  PROTECTION ACTIVE
                </span>
                <span>R-LINK · SECURE</span>
              </div>
            </div>
          </div>
        </div>

        {/* Embedded keyframes — Tailwind 3.3 doesn't ship these by default */}
        <style>{`
          @keyframes rlinkPrivRingSpin {
            0%   { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .rlink-priv-ring {
            background: conic-gradient(
              from 0deg,
              rgba(0,200,83,0.1) 0deg,
              #00c853 60deg,
              rgba(0,200,83,0.1) 120deg,
              #6a1fbf 200deg,
              rgba(106,31,191,0.1) 260deg,
              #00c853 340deg,
              rgba(0,200,83,0.1) 360deg
            );
            animation: rlinkPrivRingRotate 6s linear infinite;
          }
          @keyframes rlinkPrivRingRotate {
            to { filter: hue-rotate(360deg); }
          }
          @keyframes rlinkPrivScan {
            0%   { transform: translateY(0); opacity: 0; }
            10%  { opacity: 0.7; }
            90%  { opacity: 0.7; }
            100% { transform: translateY(100%); opacity: 0; }
          }
          .rlink-priv-scan {
            top: 0;
            animation: rlinkPrivScan 3.6s ease-in-out infinite;
          }
          @keyframes rlinkPrivPulse {
            0%   { transform: scale(0.85); opacity: 0.9; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          .rlink-priv-pulse {
            animation: rlinkPrivPulse 2.4s ease-out infinite;
          }
        `}</style>
      </div>
    </div>
  );

  return portalTarget ? createPortal(modal, portalTarget) : modal;
}
